import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { FormModal } from '../../modal/form-modal';
import { ConfirmDialog } from '../../modal/confirm-dialog';
import { AppButton } from '../../button/app-button';
import { HelperText } from '../../form-kit';
import { LinkField, LinkRow } from '../../onboarding/invite/invite-qr-modal';
import { ActionWithReason } from '../dashboard/dashboard-shared-components/disabled-reason';
import { StatusLabel } from '../../../motion';
import toast from '../../../utils/toast';
import { useAuth } from '../../../context/auth-provider';
import { markLinkSent, revokeLink, rotateLink } from '../../../api/stays-client';
import type { StayEmailOutcome, StayLinkIssue, StayReservation } from '../../../interfaces/stays';
import { refusalText } from '../dashboard/dashboard-stays-components/stays-labels';
import { FormAlert } from '../dashboard/dashboard-stays-components/dashboard-stays.styled';
import {
  duration,
  easing,
  layout,
  palette,
  radius,
  shadow,
  spacing,
  typography,
} from '../../../style-utils/theme';
import { touch } from '../../../style-utils/media-queries';
import { firstName } from './stays-reservations-model';

/**
 * THE CHECK-IN LINK OF ONE STAY (ai-plans/stays plan S3).
 *
 * The URL is shown ONCE: right after the stay is saved, or right after a new
 * link is made. The server keeps only a hash, so from "Link" on an existing
 * stay the panel says so and offers to make a new one (the old one stops
 * working), to email a new one to the guest, or to stop the live one.
 *
 * Every way the host hands the link over is recorded — Copy link, Copy
 * message and Send on WhatsApp each call markLinkSent — so the stay list can
 * say whether the guest was ever sent it. Fire-and-forget: a failed record
 * never gets in the way of the copy.
 *
 * "View as guest" (user, 2026-10-27, for demos without e-mail): after a
 * confirm, the host is signed out and the browser opens this stay's guest
 * check-in on THIS app, as the guest would. It uses the link on screen, or
 * makes a new one (the old one stops working). It is not recorded as sent:
 * the guest was not given anything.
 */

/* The guest page's path on this app: the link's own origin may differ (a
   proxy, another host name), and the host must land on the app they are in. */
export const guestPathOf = (url: string): string => {
  try {
    const u = new URL(url, window.location.origin);

    return `${u.pathname}${u.search}${u.hash}`;
  } catch {
    return url;
  }
};

export interface IssuedLink {
  link: StayLinkIssue;
  email: StayEmailOutcome;
}

export interface StayLinkPanelProps {
  open: boolean;
  reservation: StayReservation | null;
  /** The once-only link, when the panel opens straight after a save. */
  issued?: IssuedLink | null;
  onClose: () => void;
  /** A link was made or stopped: the list reads the stay again. */
  onChanged?: () => void;
}

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  min-width: 0;
`;

const Lead = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${palette.ink};
  overflow-wrap: anywhere;
`;

const Buttons = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: ${spacing.xs};
  min-width: 0;
`;

/* The invite row sits flush here: its top margin is for a centred modal. */
const PanelLinkRow = styled(LinkRow)`
  margin: 0;
`;

/* 16px on touch, so iOS does not zoom into the read-only field on focus. */
const PanelLinkField = styled(LinkField)`
  ${touch} {
    font-size: ${typography.size.base}px;
  }
`;

/* An ANCHOR that reads as AppButton's secondary variant: WhatsApp is a link
   out (target _blank), so it must be an <a>, and AppButton renders a button. */
const WhatsAppLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: ${layout.buttonHeight};
  padding: 0 ${spacing.md};
  border: 1px solid ${palette.lineStrong};
  border-radius: ${radius.sm};
  background: ${palette.surface};
  color: ${palette.ink};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  line-height: 1;
  white-space: nowrap;
  text-decoration: none;
  cursor: pointer;
  transition: color ${duration.fast} ${easing.standard}, background-color ${duration.fast} ${easing.standard},
    border-color ${duration.fast} ${easing.standard}, box-shadow ${duration.base} ${easing.standard};

  &:hover {
    background: ${palette.tealTintSoft};
    border-color: ${palette.teal};
    box-shadow: ${shadow.sm};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  ${touch} {
    height: auto;
    min-height: ${layout.touchTarget};
  }
`;

type Busy = 'rotate' | 'email' | 'revoke' | 'guest' | null;
type Copied = 'link' | 'message' | null;

export const emailOutcomeText = (email: StayEmailOutcome, masked: string | null): string | null => {
  if (email === 'sent' || email === 'spoofed') return masked ? `Sent to ${masked}.` : null;
  if (email === 'failed') return 'The email could not be sent. Copy the link instead.';

  return null;
};

export const StayLinkPanel: React.FC<StayLinkPanelProps> = ({ open, reservation, issued, onClose, onChanged }) => {
  const [current, setCurrent] = useState<IssuedLink | null>(issued || null);
  const [linkLive, setLinkLive] = useState<boolean>(reservation?.checkin.link_status === 'live');
  const [busy, setBusy] = useState<Busy>(null);
  const busyRef = useRef(false);
  const [problem, setProblem] = useState('');
  const [copied, setCopied] = useState<Copied>(null);
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [confirming, setConfirming] = useState<'rotate' | 'revoke' | 'guest' | null>(null);
  const { logout } = useAuth();

  // A fresh panel each time it opens on a stay. Keyed on the stay's id, not
  // the object: the list reads again after a new link, and that must not wipe
  // the URL that is on screen for the only time.
  const reservationId = reservation?.id;
  const reservationLinkLive = reservation?.checkin.link_status === 'live';
  useEffect(() => {
    if (!open) return;
    setCurrent(issued || null);
    setLinkLive(issued ? true : reservationLinkLive);
    setProblem('');
    setCopied(null);
    setConfirming(null);
    // reservationLinkLive is read at open only, on purpose.
  }, [open, issued, reservationId]);

  useEffect(
    () => () => {
      if (copiedTimer.current) clearTimeout(copiedTimer.current);
    },
    []
  );

  if (!reservation) return null;
  const first = firstName(reservation);
  const masked = reservation.lead.email_masked;

  const record = (via: 'copy' | 'whatsapp') => {
    markLinkSent(reservation.id, via).catch(() => {
      // Recording how the link went out is a nicety; the copy already happened.
    });
  };

  const copy = async (what: 'link' | 'message') => {
    if (!current) return;
    try {
      await navigator.clipboard.writeText(what === 'link' ? current.link.url : current.link.message_text);
      setCopied(what);
      if (copiedTimer.current) clearTimeout(copiedTimer.current);
      copiedTimer.current = setTimeout(() => setCopied(null), 1800);
      record('copy');
    } catch {
      setCopied(null);
    }
  };

  const rotate = async (sendEmail: boolean) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(sendEmail ? 'email' : 'rotate');
    setProblem('');
    try {
      const made = await rotateLink(reservation.id, sendEmail ? { send_email: true } : {});
      setCurrent({ link: made.link, email: made.email });
      setLinkLive(true);
      setCopied(null);
      toast.success(`Made a new link for ${first}. The old one no longer works.`);
      if (onChanged) onChanged();
    } catch (error) {
      setProblem(refusalText(error as { code?: string | null; message?: string }, 'Could not make a new link. Try again.'));
    } finally {
      busyRef.current = false;
      setBusy(null);
    }
  };

  const revoke = async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy('revoke');
    setProblem('');
    try {
      await revokeLink(reservation.id);
      setCurrent(null);
      setLinkLive(false);
      toast.success(`${first}'s link no longer works.`);
      if (onChanged) onChanged();
    } catch (error) {
      setProblem(refusalText(error as { code?: string | null; message?: string }, 'Could not stop the link. Try again.'));
    } finally {
      busyRef.current = false;
      setBusy(null);
    }
  };

  const viewAsGuest = async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy('guest');
    setProblem('');
    try {
      const url = current ? current.link.url : (await rotateLink(reservation.id, {})).link.url;
      await logout();
      window.location.assign(guestPathOf(url));
    } catch (error) {
      setProblem(refusalText(error as { code?: string | null; message?: string }, 'Could not open the guest view. Try again.'));
      busyRef.current = false;
      setBusy(null);
    }
  };

  const fromSave = !!issued;
  const outcome = current ? emailOutcomeText(current.email, masked) : null;

  return (
    <>
      <FormModal
        open={open}
        title={`Check-in link for ${first}`}
        onClose={onClose}
        saving={!!busy}
        footer={
          <AppButton variant="secondary" disabled={!!busy} onClick={onClose}>
            Done
          </AppButton>
        }
      >
        <Body data-testid="stay-link-panel">
          {problem ? <FormAlert role="alert">{problem}</FormAlert> : null}

          {current ? (
            <>
              <PanelLinkRow>
                <PanelLinkField
                  readOnly
                  value={current.link.url}
                  aria-label={`Check-in link for ${first}`}
                  onFocus={e => e.target.select()}
                />
                <AppButton variant="secondary" onClick={() => void copy('link')}>
                  <StatusLabel state={copied === 'link' ? 'copied' : 'copy'} labels={{ copy: 'Copy link', copied: 'Copied' }} />
                </AppButton>
              </PanelLinkRow>
              <Buttons>
                <AppButton variant="secondary" onClick={() => void copy('message')}>
                  <StatusLabel
                    state={copied === 'message' ? 'copied' : 'copy'}
                    labels={{ copy: 'Copy message', copied: 'Copied' }}
                  />
                </AppButton>
                <WhatsAppLink
                  href={current.link.whatsapp_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => record('whatsapp')}
                >
                  Send on WhatsApp
                </WhatsAppLink>
              </Buttons>
              {outcome ? (
                <Lead role="status" data-email-outcome={current.email}>
                  {outcome}
                </Lead>
              ) : null}
              <HelperText>Anyone with this link can check in as this guest. Send it only to them.</HelperText>
            </>
          ) : (
            <Lead>For security the link is shown only once.</Lead>
          )}

          <Buttons>
            <AppButton variant="secondary" disabled={!!busy} onClick={() => setConfirming('guest')}>
              <StatusLabel state={busy === 'guest' ? 'busy' : 'idle'} labels={{ idle: 'View as guest', busy: 'Opening…' }} />
            </AppButton>
          </Buttons>

          {!fromSave ? (
            <Buttons>
              <AppButton
                variant={current ? 'secondary' : 'primary'}
                disabled={!!busy}
                onClick={() => setConfirming('rotate')}
              >
                <StatusLabel state={busy === 'rotate' ? 'busy' : 'idle'} labels={{ idle: 'Make a new link', busy: 'Making a link…' }} />
              </AppButton>
              <ActionWithReason reason={masked ? null : "Add the guest's email first"}>
                {describedBy => (
                  <AppButton
                    variant="secondary"
                    disabled={!!busy || !masked}
                    aria-describedby={describedBy}
                    onClick={() => void rotate(true)}
                  >
                    <StatusLabel
                      state={busy === 'email' ? 'busy' : 'idle'}
                      labels={{ idle: masked ? `Email it to ${masked}` : 'Email it to the guest', busy: 'Sending…' }}
                    />
                  </AppButton>
                )}
              </ActionWithReason>
              {linkLive ? (
                <AppButton variant="destructive" disabled={!!busy} onClick={() => setConfirming('revoke')}>
                  Stop this link
                </AppButton>
              ) : null}
            </Buttons>
          ) : null}
        </Body>
      </FormModal>

      <ConfirmDialog
        open={confirming === 'rotate'}
        title="Make a new link?"
        message="The old link stops working."
        primaryLabel="Make a new link"
        onPrimary={async () => {
          await rotate(false);
          setConfirming(null);
        }}
        onDismiss={() => setConfirming(null)}
      />
      <ConfirmDialog
        open={confirming === 'guest'}
        title="View as guest?"
        message={
          current
            ? `You will be signed out of Lobbero and ${first}'s check-in opens as the guest sees it. Whatever you enter there is saved as ${first}'s check-in. Sign in again when you are done.`
            : `This makes a new link for ${first} (the old one stops working), signs you out of Lobbero and opens the check-in as the guest sees it. Whatever you enter there is saved as ${first}'s check-in.`
        }
        primaryLabel="Sign out and view"
        onPrimary={async () => {
          setConfirming(null);
          await viewAsGuest();
        }}
        onDismiss={() => setConfirming(null)}
      />
      <ConfirmDialog
        open={confirming === 'revoke'}
        title="Stop this link?"
        message={`${first} can no longer check in with it. You can make a new link later.`}
        primaryLabel="Stop this link"
        primaryVariant="destructive"
        onPrimary={async () => {
          await revoke();
          setConfirming(null);
        }}
        onDismiss={() => setConfirming(null)}
      />
    </>
  );
};
